import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { SingleValue } from 'react-select';

import { ApolloError } from '@apollo/client';
import styled from '@emotion/styled';
import { capitalize } from 'lodash';

import { Box } from '@clutter/clean';

import { Alert, Button, Modal } from '@shared/components/bootstrap';

import { NOT_LISTED_ROW_LABEL } from '@admin/components/estimation/order/constants';
import { QuantityStepper } from '@admin/components/estimation/order/quantity_stepper';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import {
  Estimation__CategoryType,
  Estimation__OrderItemEstimation,
  EstimationsForOrderDocument,
  OrderStateEnum,
  OrderTypeEnum,
  Status,
  useEstimationEstimatedItemsCreateMutation,
  useEstimationsForOrderQuery,
} from '@admin/schema';

const TABLE_MAX_WIDTH = '900px';
const StyledData = styled.td<{ selected: boolean }>`
  font-weight: ${({ selected }) => (selected ? 'bold' : 'medium')};
  text-align: left;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 20px;
  padding-bottom: 8px;
`;

const AlertContainer = styled(Box)`
  max-width: ${TABLE_MAX_WIDTH};
`;

const cuftTotalDisplay = (cuft?: number | null, quantity?: number | null) => {
  if (cuft == null || quantity == null || quantity < 1 || !Number.isFinite(cuft) || !Number.isFinite(quantity)) {
    return '-';
  }
  const product = cuft * quantity;
  return Number.isFinite(product) ? product.toFixed(2) : '-';
};

const formatTotalCuft = (value: number) => (Number.isFinite(value) ? value.toFixed(2) : '—');

type RangeCategory = {
  id: string;
  name: string;
  defaultCuft?: number | null;
};

type AgentInputCategory = {
  categoryID: string;
  name: string;
  quantity: number;
  cuft?: number | null;
  /** Free-form description for not-listed items (persisted as EstimatedItem#notes). */
  description?: string | null;
};

type QuantityRange = {
  lower: number;
  upper: number;
};

type RangeCustomerEstimation = Pick<
  Estimation__OrderItemEstimation,
  'categoryID' | 'name' | 'cuft' | 'otherItemDescription'
>;

type Option = {
  value: string;
  label: string;
  cuft?: number | null;
};

type ColumnMode = 'customer' | 'agent';

/** Subset of estimationsForOrder used to sync agent estimation local state from the server. */
type EstimationsForOrderSyncPayload = {
  itemEstimations?: Estimation__OrderItemEstimation[] | null;
  agentBoxEstimation?: (Partial<AgentInputCategory> & { name?: string | null }) | null;
  agentOtherItemEstimation?:
    | (Partial<AgentInputCategory> & { name?: string | null; otherItemDescription?: string | null })
    | null;
};

const getQuantityRange = (name?: string): QuantityRange => {
  if (!name || name === 'None') {
    return { lower: 0, upper: 0 };
  } else if (name.includes('-')) {
    const range = name.split('-');
    const lowerRange = +range[0];
    const upperRange = +range[1];
    return { lower: isNaN(lowerRange) ? 0 : lowerRange, upper: isNaN(upperRange) ? 0 : upperRange };
  } else {
    return { lower: isNaN(+name) ? 0 : +name, upper: isNaN(+name) ? 0 : +name };
  }
};

const quantityTotalDisplay = (currentQuantity: number, boxName?: string, otherItemName?: string) => {
  const boxesRange = getQuantityRange(boxName);
  const otherItemsRange = getQuantityRange(otherItemName);
  const lowerTotal = boxesRange.lower + otherItemsRange.lower + currentQuantity;
  const upperTotal = boxesRange.upper + otherItemsRange.upper + currentQuantity;
  if (lowerTotal === upperTotal) return lowerTotal;
  else return `${lowerTotal} - ${upperTotal}`;
};

const convertSelectionToInput = (category: RangeCategory): AgentInputCategory => {
  const { id, name, defaultCuft } = category;
  return { name, categoryID: id, cuft: defaultCuft, quantity: 1 };
};

const buildAgentSelections = (itemEstimations: Estimation__OrderItemEstimation[]) =>
  itemEstimations.reduce((obj, estimation) => {
    const { categoryID, name, agentQuantity, cuft } = estimation;
    if (agentQuantity > 0) obj.set(estimation.categoryID, { categoryID, name, cuft, quantity: agentQuantity });
    return obj;
  }, new Map<string, AgentInputCategory>());

function syncEstimationsForOrderToAgentState(
  efo: EstimationsForOrderSyncPayload | null | undefined,
  setAgentSelections: React.Dispatch<React.SetStateAction<Map<string, AgentInputCategory> | undefined>>,
  setAgentBoxSelection: React.Dispatch<React.SetStateAction<AgentInputCategory | null | undefined>>,
  setAgentOtherItemSelection: React.Dispatch<React.SetStateAction<AgentInputCategory | null | undefined>>,
): void {
  if (!efo) {
    setAgentSelections(new Map());
    setAgentBoxSelection(null);
    setAgentOtherItemSelection(null);
    return;
  }
  const { itemEstimations, agentBoxEstimation, agentOtherItemEstimation } = efo;
  if (itemEstimations) {
    setAgentSelections(buildAgentSelections(itemEstimations));
  } else {
    setAgentSelections(new Map());
  }
  if (agentBoxEstimation) {
    setAgentBoxSelection({ ...agentBoxEstimation, quantity: 1 } as AgentInputCategory);
  } else {
    setAgentBoxSelection(null);
  }
  if (agentOtherItemEstimation) {
    setAgentOtherItemSelection({
      ...agentOtherItemEstimation,
      quantity: 1,
      description: agentOtherItemEstimation.otherItemDescription ?? null,
    } as AgentInputCategory);
  } else {
    setAgentOtherItemSelection(null);
  }
}

const hasMeaningfulBoxOrOtherName = (name?: string | null) => Boolean(name && name !== 'None' && name !== '0');

const DropdownItemEstimationRow: React.FC<{
  categoryName: string;
  columnMode: ColumnMode;
  editing: boolean;
  categories: Estimation__CategoryType[];
  agentSelection?: AgentInputCategory | null;
  customerSelection?: RangeCustomerEstimation | null;
  setSelection: React.Dispatch<React.SetStateAction<AgentInputCategory | null | undefined>>;
  showNotListedDescription?: boolean;
}> = ({
  categoryName,
  columnMode,
  editing,
  categories,
  agentSelection,
  customerSelection,
  setSelection,
  showNotListedDescription = false,
}) => {
  const hasAgentSelection = hasMeaningfulBoxOrOtherName(agentSelection?.name);
  const hasCustomerSelection = customerSelection
    ? customerSelection.name !== 'None' && customerSelection.name !== '0'
    : false;

  if (columnMode === 'customer') {
    const customerDescription =
      showNotListedDescription && customerSelection?.otherItemDescription
        ? customerSelection.otherItemDescription
        : null;
    return (
      <tr>
        <StyledData selected={hasCustomerSelection}>
          {categoryName}
          {customerDescription && (
            <>
              <br />
              <span className="text-muted">{customerDescription}</span>
            </>
          )}
        </StyledData>
        <StyledData selected={hasCustomerSelection}>{hasCustomerSelection ? customerSelection?.cuft : '-'}</StyledData>
        <StyledData selected={hasCustomerSelection}>{hasCustomerSelection ? customerSelection?.name : '-'}</StyledData>
      </tr>
    );
  }

  return (
    <tr>
      <StyledData selected={hasAgentSelection}>
        {categoryName}
        {showNotListedDescription && editing && (
          <input
            className="form-control mar-top"
            placeholder="Describe the item(s)"
            value={agentSelection?.description ?? ''}
            onChange={(event) => {
              setSelection((current) => (current ? { ...current, description: event.target.value } : current));
            }}
          />
        )}
        {showNotListedDescription && !editing && agentSelection?.description && (
          <>
            <br />
            <span className="text-muted">{agentSelection.description}</span>
          </>
        )}
      </StyledData>
      <StyledData selected={hasAgentSelection}>{hasAgentSelection ? (agentSelection?.cuft ?? '-') : '-'}</StyledData>
      <StyledData selected={hasAgentSelection}>
        {editing ? (
          <select
            className="form-control"
            value={agentSelection?.categoryID ?? ''}
            onChange={(event) => {
              const selectedCategory = categories.find((category) => category.id === event.target.value);
              setSelection(selectedCategory ? convertSelectionToInput(selectedCategory) : undefined);
            }}
          >
            <option label="-" value="">
              -
            </option>
            {categories.map(({ id, name }) => (
              <option key={id} label={name} value={id}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <span>{hasAgentSelection ? (agentSelection?.name ?? '') : '-'}</span>
        )}
      </StyledData>
    </tr>
  );
};

const ItemEstimationRow: React.FC<{
  itemEstimation: Estimation__OrderItemEstimation;
  columnMode: ColumnMode;
  agentSelections: Map<string, AgentInputCategory>;
  editing: boolean;
  setAgentSelections: React.Dispatch<React.SetStateAction<Map<string, AgentInputCategory> | undefined>>;
}> = ({ itemEstimation, columnMode, agentSelections, editing, setAgentSelections }) => {
  const { categoryID, name, agentQuantity, customerQuantity, cuft } = itemEstimation;
  const agentSelection = agentSelections?.get(categoryID);
  const agentSelected = !!((agentQuantity && agentQuantity > 0) || (agentSelection && agentSelection.quantity > 0));
  const customerSelected = !!(customerQuantity && customerQuantity > 0);

  if (columnMode === 'customer') {
    if (!customerSelected) return null;
  } else if (!agentSelected) {
    return null;
  }

  const onItemEstimationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const quantity = parseInt(raw, 10);
    setAgentSelections((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      if (raw === '' || Number.isNaN(quantity)) {
        next.delete(categoryID);
        return next;
      }
      const existing = next.get(categoryID);
      next.set(categoryID, {
        name,
        categoryID,
        cuft,
        ...existing,
        quantity,
      });
      return next;
    });
  };

  if (columnMode === 'customer') {
    return (
      <tr key={categoryID}>
        <StyledData selected={customerSelected}>{name}</StyledData>
        <StyledData selected={customerSelected}>{cuftTotalDisplay(cuft, customerQuantity)}</StyledData>
        <StyledData selected={customerSelected}>{customerSelected ? customerQuantity : '-'}</StyledData>
      </tr>
    );
  }

  return (
    <tr key={categoryID}>
      <StyledData selected={agentSelected}>{name}</StyledData>
      <StyledData selected={agentSelected}>
        {cuftTotalDisplay(agentSelection?.cuft, agentSelection?.quantity)}
      </StyledData>
      <StyledData selected={agentSelected}>
        {editing && agentSelection && agentSelection.quantity > 0 ? (
          <QuantityStepper
            value={agentSelection.quantity}
            onChange={(quantity) => {
              setAgentSelections((prev) => {
                if (!prev) return prev;
                const next = new Map(prev);
                next.set(categoryID, { ...agentSelection, quantity });
                return next;
              });
            }}
          />
        ) : editing ? (
          <input value="" placeholder="-" readOnly />
        ) : (
          <span>{agentSelection?.quantity ?? '-'}</span>
        )}
      </StyledData>
    </tr>
  );
};

const NewEstimatedItemRow: React.FC<{
  itemEstimations?: Estimation__OrderItemEstimation[];
  agentSelections?: Map<string, AgentInputCategory>;
  setAgentSelections: React.Dispatch<React.SetStateAction<Map<string, AgentInputCategory> | undefined>>;
  handleScroll(): void;
}> = ({ itemEstimations, agentSelections, setAgentSelections, handleScroll }) => {
  const [newSelection, setNewSelection] = useState<AgentInputCategory>();
  const [selectedOption, setSelectedOption] = useState<SingleValue<Option>>(null);

  const options: Option[] = useMemo(
    () =>
      Array.from(itemEstimations || [])
        .filter((selection) => (selection.agentQuantity ?? 0) === 0)
        .filter((selection) => {
          const addedQty = agentSelections?.get(selection.categoryID)?.quantity ?? 0;
          return addedQty <= 0;
        })
        .map((selection) => ({
          value: selection.categoryID,
          label: selection.name,
          cuft: selection.cuft,
        })),
    [agentSelections, itemEstimations],
  );

  return (
    <tr>
      <StyledData selected={false}>
        <span onClick={() => handleScroll()}>
          <Select
            classNamePrefix="Select"
            value={selectedOption}
            onChange={(option: SingleValue<Option>) => {
              setSelectedOption(option);
              if (option == null) {
                setNewSelection(undefined);
                return;
              }
              const { value, label, cuft } = option;
              setNewSelection(agentSelections?.get(value) ?? { categoryID: value, name: label, cuft, quantity: 1 });
            }}
            options={options}
            isClearable
          />
        </span>
      </StyledData>
      <StyledData selected={false}>{cuftTotalDisplay(newSelection?.cuft, newSelection?.quantity)}</StyledData>
      <StyledData selected>
        <Box.Flex justifyContent="space-between" alignContent="row" alignItems="center">
          {newSelection && newSelection.quantity > 0 ? (
            <QuantityStepper
              value={newSelection.quantity}
              onChange={(quantity) => {
                setNewSelection((current) => (current ? { ...current, quantity } : current));
              }}
            />
          ) : (
            <span>-</span>
          )}
          <Button
            kind="primary"
            disabled={!newSelection || newSelection.quantity < 1}
            onClick={() => {
              if (newSelection && newSelection.quantity > 0) {
                setAgentSelections((prev) => {
                  const base = prev ?? new Map<string, AgentInputCategory>();
                  return new Map(base).set(newSelection.categoryID, newSelection);
                });
                setNewSelection(undefined);
                setSelectedOption(null);
              }
            }}
          >
            Add
          </Button>
        </Box.Flex>
      </StyledData>
    </tr>
  );
};

export const OrderPanel: React.FC<{
  orderID: string;
  algorithmID: string;
  isVirtualWalkthrough: boolean;
  columnMode: ColumnMode;
  /** Customer estimation algorithm to copy when pre-filling agent estimation (Customer tab source). */
  prefillSourceAlgorithmID?: string;
}> = ({ orderID, algorithmID, isVirtualWalkthrough, columnMode, prefillSourceAlgorithmID }) => {
  const readOnly = columnMode === 'customer';
  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState<boolean>(false);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [agentSelections, setAgentSelections] = useState<Map<string, AgentInputCategory>>();
  const [agentBoxSelection, setAgentBoxSelection] = useState<AgentInputCategory | null>();
  const [agentOtherItemSelection, setAgentOtherItemSelection] = useState<AgentInputCategory | null>();
  const [estimatedItemsCreate, { loading }] = useEstimationEstimatedItemsCreateMutation({ client });
  const {
    data,
    refetch,
    error: estimationsQueryError,
    loading: estimationsQueryLoading,
  } = useEstimationsForOrderQuery({
    client,
    skip: !algorithmID,
    variables: { orderID, algorithmID: algorithmID! },
    // Agent tab must reflect exactly what was persisted; avoid showing cached totals that later
    // reconcile with network (e.g. after save) or merge with stale entries.
    fetchPolicy: columnMode === 'agent' ? 'network-only' : 'cache-first',
  });

  useEffect(() => {
    if (!algorithmID || estimationsQueryLoading) return;
    if (columnMode === 'agent' && editing) return;
    syncEstimationsForOrderToAgentState(
      data?.estimationsForOrder ?? null,
      setAgentSelections,
      setAgentBoxSelection,
      setAgentOtherItemSelection,
    );
  }, [algorithmID, columnMode, data, editing, estimationsQueryLoading, orderID]);

  const scrollDivRef = useRef<HTMLDivElement>(null);
  const itemEstimationsMasterRef = useRef<Estimation__OrderItemEstimation[]>([]);

  useEffect(() => {
    const rows = data?.estimationsForOrder?.itemEstimations;
    if (rows && rows.length > 0) {
      itemEstimationsMasterRef.current = rows;
    }
  }, [data?.estimationsForOrder?.itemEstimations]);

  if (!algorithmID) {
    return null;
  }

  if (estimationsQueryError) {
    return (
      <AlertContainer>
        <Box.Flex flexDirection="column" style={{ gap: '8px' }}>
          <Alert style="danger">
            Failed to load order estimations
            {estimationsQueryError.message ? `: ${estimationsQueryError.message}` : ''}
          </Alert>
          <Button kind="primary" onClick={() => void refetch()}>
            Retry
          </Button>
        </Box.Flex>
      </AlertContainer>
    );
  }

  if (estimationsQueryLoading) {
    return <Spinner />;
  }
  if (!data) {
    return <Spinner />;
  }

  const {
    agentBoxEstimation = null,
    customerBoxEstimation = null,
    itemEstimations = [],
    agentOtherItemEstimation = null,
    customerOtherItemEstimation = null,
    roomEstimations = [],
    allItemsInListedRooms = false,
    itemsRequireMoreThanOneMover = null,
  } = data.estimationsForOrder ?? {};
  const { boxCategories, otherItemCategories, order } = data;

  const itemEstimationsForNewRow = itemEstimations.length > 0 ? itemEstimations : itemEstimationsMasterRef.current;

  const canEdit = !readOnly && order.type !== OrderTypeEnum.Move && order.state === OrderStateEnum.Approved;

  const agentSelectedItemCount = () =>
    Array.from(agentSelections?.values() ?? []).reduce((total, item) => {
      total += item.quantity;
      return total;
    }, 0);

  const agentTotalItemCuft = () => {
    let cuftTotal = Array.from(agentSelections?.values() ?? []).reduce((total, item) => {
      total += item.quantity * (item.cuft ?? 0);
      return total;
    }, 0);
    cuftTotal += agentOtherItemSelection?.cuft ?? 0;
    cuftTotal += agentBoxSelection?.cuft ?? 0;
    return cuftTotal;
  };

  let customerTotalItems = 0;
  let customerTotalItemCuft = 0;
  itemEstimations.forEach(({ customerQuantity, cuft }) => {
    if (customerQuantity && customerQuantity > 0) {
      customerTotalItems += customerQuantity;
      customerTotalItemCuft += customerQuantity * (cuft ?? 0);
    }
  });
  customerTotalItemCuft += customerBoxEstimation?.cuft ?? 0;
  customerTotalItemCuft += customerOtherItemEstimation?.cuft ?? 0;
  let requireMoreThanOneMoverAnswer = 'Not Answered';
  if (itemsRequireMoreThanOneMover) {
    requireMoreThanOneMoverAnswer = itemsRequireMoreThanOneMover.split('_').map(capitalize).join(' ');
  }

  const hasExistingAgentEstimation = (): boolean => {
    if (itemEstimations.some((i) => (i.agentQuantity ?? 0) > 0)) return true;
    if (hasMeaningfulBoxOrOtherName(agentBoxEstimation?.name)) return true;
    if (hasMeaningfulBoxOrOtherName(agentOtherItemEstimation?.name)) return true;
    return false;
  };

  const handleCancel = () => {
    setAgentSelections(buildAgentSelections(itemEstimations));
    setAgentBoxSelection(agentBoxEstimation ? { ...agentBoxEstimation, quantity: 1 } : null);
    setAgentOtherItemSelection(
      agentOtherItemEstimation
        ? {
            categoryID: agentOtherItemEstimation.categoryID ?? '',
            name: agentOtherItemEstimation.name ?? '',
            cuft: agentOtherItemEstimation.cuft,
            quantity: 1,
            description: agentOtherItemEstimation.otherItemDescription ?? null,
          }
        : null,
    );
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      const response = await estimatedItemsCreate({
        variables: {
          input: {
            orderID,
            algorithmID,
            otherItemCategoryName: agentOtherItemSelection?.name,
            otherItemDescription: agentOtherItemSelection?.description?.trim() || undefined,
            boxCategoryName: agentBoxSelection?.name,
            itemInputs: Array.from(agentSelections?.values() ?? [])
              .filter(({ quantity }) => quantity > 0)
              .map(({ categoryID, quantity }) => ({
                categoryID,
                quantity,
              })),
          },
        },
      });

      if (response.data?.result?.error) {
        setError(response.data.result.error);
      }

      if (response.data?.result?.status === Status.Ok) {
        setError(undefined);
        setEditing(false);
        await refetch();
      }
    } catch (e: unknown) {
      const message =
        (e instanceof ApolloError && e.message) ||
        (e instanceof Error && e.message) ||
        'An unknown error occurred. Please contact technical support.';
      setError(message);
    }
  };

  const prefillFromCustomer = async () => {
    if (!prefillSourceAlgorithmID) return;
    setPrefillLoading(true);
    setError(undefined);
    try {
      const sameAlgorithm = prefillSourceAlgorithmID === algorithmID;
      const [sourceResult, targetResult] = await Promise.all([
        client.query({
          query: EstimationsForOrderDocument,
          variables: { orderID, algorithmID: prefillSourceAlgorithmID },
          fetchPolicy: 'network-only',
        }),
        sameAlgorithm
          ? Promise.resolve({ data: undefined })
          : client.query({
              query: EstimationsForOrderDocument,
              variables: { orderID, algorithmID },
              fetchPolicy: 'network-only',
            }),
      ]);
      const efo = sourceResult.data?.estimationsForOrder;
      if (!efo) {
        setError('Could not load customer estimation to pre-fill.');
        return;
      }
      const targetItemEstimations =
        (sameAlgorithm ? efo.itemEstimations : targetResult.data?.estimationsForOrder?.itemEstimations) ?? [];
      const targetCategoryIdByName = new Map<string, string>(
        targetItemEstimations.map(
          (ti: Estimation__OrderItemEstimation) => [String(ti.name), String(ti.categoryID)] as [string, string],
        ),
      );
      const targetCategoryIdSet = new Set(
        targetItemEstimations.map((ti: Estimation__OrderItemEstimation) => String(ti.categoryID)),
      );

      /** When customer vs agent use different algorithms, same ItemCategory is often shared; prefer id match, then name. */
      const resolvePrefillTargetCategoryId = (i: Estimation__OrderItemEstimation): string | undefined => {
        if (sameAlgorithm) return i.categoryID;
        if (i.categoryID != null && i.categoryID !== '' && targetCategoryIdSet.has(String(i.categoryID))) {
          return i.categoryID;
        }
        return targetCategoryIdByName.get(String(i.name));
      };

      const { itemEstimations: customerItems, customerBoxEstimation: cbe, customerOtherItemEstimation: coe } = efo;
      const customerLineItems = (customerItems ?? []).filter(
        (i: Estimation__OrderItemEstimation) => (i.customerQuantity ?? 0) > 0,
      );
      const unmappedCustomerItems = customerLineItems.filter((i: Estimation__OrderItemEstimation) => {
        const categoryID = resolvePrefillTargetCategoryId(i);
        return categoryID == null || categoryID === '';
      });
      if (unmappedCustomerItems.length > 0) {
        const detail = unmappedCustomerItems
          .map((i: Estimation__OrderItemEstimation) => `${i.name} (category ${i.categoryID})`)
          .join('; ');
        setError(`Cannot pre-fill: no matching agent category for customer items: ${detail}.`);
        return;
      }
      const itemInputs = customerLineItems.map((i: Estimation__OrderItemEstimation) => ({
        categoryID: resolvePrefillTargetCategoryId(i) as string,
        quantity: i.customerQuantity as number,
      }));
      const response = await estimatedItemsCreate({
        variables: {
          input: {
            orderID,
            algorithmID,
            boxCategoryName: hasMeaningfulBoxOrOtherName(cbe?.name) ? cbe?.name : undefined,
            otherItemCategoryName: hasMeaningfulBoxOrOtherName(coe?.name) ? coe?.name : undefined,
            itemInputs,
          },
        },
      });
      if (response.data?.result?.error) {
        setError(response.data.result.error);
        return;
      }
      if (response.data?.result?.status === Status.Ok) {
        setError(undefined);
        setShowPrefillModal(false);
        const refetchResult = await refetch();
        const refreshed = refetchResult.data?.estimationsForOrder;
        if (refreshed) {
          syncEstimationsForOrderToAgentState(
            refreshed,
            setAgentSelections,
            setAgentBoxSelection,
            setAgentOtherItemSelection,
          );
        }
        setEditing(true);
      }
    } catch (e: unknown) {
      const message =
        (e instanceof ApolloError && e.message) ||
        (e instanceof Error && e.message) ||
        'An unknown error occurred. Please contact technical support.';
      setError(message);
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleEditClick = () => {
    if (columnMode !== 'agent') return;
    if (hasExistingAgentEstimation()) {
      setAgentSelections((prev) => prev ?? buildAgentSelections(itemEstimations));
      setEditing(true);
    } else {
      setShowPrefillModal(true);
    }
  };

  const startFromScratch = () => {
    setError(undefined);
    setShowPrefillModal(false);
    setAgentSelections(new Map());
    setAgentBoxSelection(null);
    setAgentOtherItemSelection(null);
    setEditing(true);
  };

  const scrollToBottom = () => {
    if (scrollDivRef.current) {
      scrollDivRef.current.scrollTop = scrollDivRef.current.scrollHeight;
    }
  };

  const showBoxesRow =
    columnMode === 'customer'
      ? Boolean(
          customerBoxEstimation && boxCategories.length > 0 && hasMeaningfulBoxOrOtherName(customerBoxEstimation.name),
        )
      : Boolean(boxCategories.length > 0 && (editing || hasMeaningfulBoxOrOtherName(agentBoxSelection?.name)));

  const showOtherRow =
    columnMode === 'customer'
      ? Boolean(
          customerOtherItemEstimation &&
          otherItemCategories.length > 0 &&
          hasMeaningfulBoxOrOtherName(customerOtherItemEstimation.name),
        )
      : Boolean(
          otherItemCategories.length > 0 && (editing || hasMeaningfulBoxOrOtherName(agentOtherItemSelection?.name)),
        );

  return (
    <>
      <Box.Flex justifyContent="space-between" alignContent="row" maxWidth={TABLE_MAX_WIDTH}>
        <h3 className="text-thin">Item Selection</h3>
        <Buttons>
          {editing ? (
            <>
              <Button kind="danger" loading={loading} onClick={handleCancel}>
                Cancel
              </Button>
              <Button kind="primary" loading={loading} onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            canEdit && (
              <Button kind="primary" onClick={handleEditClick}>
                Edit
              </Button>
            )
          )}
        </Buttons>
      </Box.Flex>
      {error && (
        <AlertContainer>
          <Alert style="danger">{error}</Alert>
        </AlertContainer>
      )}
      <div
        ref={scrollDivRef}
        className="table-responsive"
        style={{ maxWidth: TABLE_MAX_WIDTH, maxHeight: '500px', overflowY: 'scroll', overscrollBehavior: 'contain' }}
      >
        <table className="table table-striped">
          <thead>
            <tr key="header">
              <th className="text-left">Name</th>
              <th className="text-left">Estimated Cuft</th>
              <th className="text-left">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {showBoxesRow && (
              <DropdownItemEstimationRow
                categoryName="Boxes"
                columnMode={columnMode}
                agentSelection={agentBoxSelection}
                customerSelection={customerBoxEstimation}
                editing={editing}
                categories={boxCategories}
                setSelection={setAgentBoxSelection}
              />
            )}
            {agentSelections &&
              itemEstimations.map((option) => (
                <ItemEstimationRow
                  key={option.categoryID}
                  columnMode={columnMode}
                  itemEstimation={option}
                  agentSelections={agentSelections}
                  editing={editing}
                  setAgentSelections={setAgentSelections}
                />
              ))}
            {showOtherRow && (
              <DropdownItemEstimationRow
                categoryName={NOT_LISTED_ROW_LABEL}
                columnMode={columnMode}
                agentSelection={agentOtherItemSelection}
                customerSelection={customerOtherItemEstimation}
                editing={editing}
                categories={otherItemCategories}
                setSelection={setAgentOtherItemSelection}
                showNotListedDescription
              />
            )}
            {columnMode === 'agent' && editing && (
              <NewEstimatedItemRow
                itemEstimations={itemEstimationsForNewRow}
                agentSelections={agentSelections}
                setAgentSelections={setAgentSelections}
                handleScroll={scrollToBottom}
              />
            )}
            <tr>
              <td className="text-left">
                <strong>Total</strong>
              </td>
              <td className="text-left">
                <strong>
                  {columnMode === 'customer'
                    ? formatTotalCuft(customerTotalItemCuft)
                    : formatTotalCuft(agentTotalItemCuft())}
                </strong>
              </td>
              <td className="text-left">
                <strong>
                  {columnMode === 'customer'
                    ? quantityTotalDisplay(
                        customerTotalItems,
                        customerBoxEstimation?.name,
                        customerOtherItemEstimation?.name,
                      )
                    : quantityTotalDisplay(
                        agentSelectedItemCount(),
                        agentBoxSelection?.name,
                        agentOtherItemSelection?.name,
                      )}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {columnMode === 'customer' && (
        <>
          <hr />
          <p>
            Do any of your items require more than 1 person to carry?&nbsp;
            <strong>{requireMoreThanOneMoverAnswer}</strong>
          </p>
        </>
      )}
      {!isVirtualWalkthrough && columnMode === 'customer' && (
        <>
          <hr />
          <h3 className="text-thin">Room Selection</h3>
          <div className="table-responsive" style={{ maxWidth: '400px' }}>
            <table className="table table-striped">
              <thead>
                <tr key="header">
                  <th className="text-left">Name</th>
                  <th className="text-left">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {roomEstimations.map((option) => (
                  <tr key={option.categoryID}>
                    <StyledData selected={option.quantity > 0}>{option.name}</StyledData>
                    <StyledData selected={option.quantity > 0}>{option.quantity}</StyledData>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Do the rooms above contain all the items that you plan to store?&nbsp;
            <strong>{allItemsInListedRooms ? 'Yes' : 'No'}</strong>
          </p>
        </>
      )}
      {columnMode === 'agent' && showPrefillModal && (
        <Modal onClose={() => !prefillLoading && setShowPrefillModal(false)}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Start agent estimation</Modal.Title>
              <Modal.Close close={() => !prefillLoading && setShowPrefillModal(false)} />
            </Modal.Header>
            <Modal.Body>
              <p>Pre-fill from the customer estimation on this order, or start with an empty agent estimation.</p>
              <Box.Flex flexDirection="column" style={{ gap: '8px' }}>
                <Button
                  kind="primary"
                  disabled={!prefillSourceAlgorithmID || prefillLoading}
                  loading={prefillLoading}
                  onClick={prefillFromCustomer}
                >
                  Pre-fill from customer estimation
                </Button>
                <Button kind="default" disabled={prefillLoading} onClick={startFromScratch}>
                  Start from scratch
                </Button>
              </Box.Flex>
            </Modal.Body>
          </Modal.Content>
        </Modal>
      )}
    </>
  );
};
